import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useApp } from '@/contexts/AppContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

export default function Account() {
  const { t, user, logout, api } = useApp();
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [consignments, setConsignments] = useState([]);

  useEffect(() => {
    if (!user) return;
    api.get('/orders/mine').then((r) => setOrders(r.data || [])).catch(() => {});
    api.get('/wishlist').then((r) => setWishlist(r.data || [])).catch(() => {});
    api.get('/consignments/mine').then((r) => setConsignments(r.data || [])).catch(() => {});
  }, [user, api]);

  if (!user) return <Navigate to="/login" replace />;

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-10 py-16" data-testid="account-page">
      <div className="flex justify-between items-end border-b border-border pb-8 mb-10">
        <div>
          <span className="overline text-muted-foreground">{t('account.title')}</span>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">{user.name}</h1>
          <p className="font-light text-muted-foreground mt-2">{user.email}</p>
        </div>
        <button onClick={logout} className="overline link-underline" data-testid="logout-btn">{t('account.signout')}</button>
      </div>

      {user.role === 'admin' && (
        <div className="mb-10 border border-accent bg-accent/5 p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="admin-cta">
          <div>
            <div className="overline text-accent">Administrator Access</div>
            <div className="font-display text-xl mt-1">Open the Admin Console</div>
            <p className="text-sm font-light text-muted-foreground mt-1">Manage products, consignments, orders, and social channel credentials.</p>
          </div>
          <Link to="/admin" className="bg-foreground text-background px-6 py-3 overline hover:opacity-80 transition whitespace-nowrap" data-testid="open-admin">Open Admin →</Link>
        </div>
      )}

      <Tabs defaultValue="orders">
        <TabsList className="bg-transparent justify-start gap-8 border-b border-border rounded-none w-full h-auto p-0">
          <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground px-0 pb-3 overline" data-testid="tab-orders">{t('account.orders')}</TabsTrigger>
          <TabsTrigger value="wishlist" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground px-0 pb-3 overline" data-testid="tab-wishlist">{t('account.wishlist')}</TabsTrigger>
          <TabsTrigger value="consignments" className="rounded-none data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-foreground px-0 pb-3 overline" data-testid="tab-consignments">{t('account.consignments')}</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-10">
          {orders.length === 0 ? <p className="font-light text-muted-foreground" data-testid="no-orders">No orders yet.</p> : (
            <div className="space-y-6">
              {orders.map((o) => (
                <div key={o.id} className="border border-border p-6" data-testid={`order-${o.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <div className="overline text-muted-foreground">Order #{o.id.slice(0,8).toUpperCase()}</div>
                      <div className="font-display text-xl mt-1">${o.amount?.toLocaleString()} · {o.payment_method}</div>
                    </div>
                    <span className="overline text-accent">{o.status}</span>
                  </div>
                  <div className="mt-4 text-sm font-light text-muted-foreground">{(o.items || []).map((i) => i.title).join(', ')}</div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="wishlist" className="mt-10">
          {wishlist.length === 0 ? <p className="font-light text-muted-foreground" data-testid="no-wishlist">No saved items.</p> : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-12">
              {wishlist.map((p) => (
                <Link key={p.id} to={`/product/${p.id}`} className="block">
                  <div className="aspect-[4/5] bg-muted overflow-hidden"><img src={p.images?.[0]} alt={p.title} className="w-full h-full object-cover" /></div>
                  <div className="mt-3 overline text-muted-foreground">{p.designer}</div>
                  <div className="font-display">{p.title}</div>
                </Link>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="consignments" className="mt-10">
          {consignments.length === 0 ? <p className="font-light text-muted-foreground" data-testid="no-consignments">No consignments yet. <Link to="/sell" className="link-underline text-accent">Start one →</Link></p> : (
            <div className="space-y-4">
              {consignments.map((c) => (
                <div key={c.id} className="border border-border p-6 flex justify-between items-center" data-testid={`consign-${c.id}`}>
                  <div>
                    <div className="font-display text-lg">{c.designer} — {c.category}</div>
                    <div className="overline text-muted-foreground mt-1">Submitted {new Date(c.created_at).toLocaleDateString()}</div>
                  </div>
                  <span className="overline text-accent">{c.status}</span>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
